import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import styles from './EditMeasurementDialog.module.scss';
import { getAbsoluteTimestamp } from '../../../../util/formatDate';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { OutlinedInput } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingButton from '@mui/lab/LoadingButton';
import FormAlert from '../FormAlert/FormAlert';
import { isNumeric } from '../../../../hooks/useTreeEditor';
import APIClient from '../../../../util/APIClient';
import { useParams } from 'react-router-dom';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

function EditMeasurementDialog({
  isOpen,
  close,
  goalLabel,
  goalMeasurement,
  reload,
  path,
}) {
  const [timeInput, setTimeInput] = React.useState(
    new Date(goalMeasurement.time),
  );
  const [valueInput, setValueInput] = React.useState(
    `${goalMeasurement.value}`,
  );
  const [valueError, setValueError] = React.useState(null);
  const [formAlert, setFormAlert] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (valueInput !== '') {
      if (!isNumeric(valueInput)) {
        setValueError('Value must be a number.');
        return;
      }
    }
    setValueError(null);
  }, [valueInput]);

  const handleClose = () => {
    setValueInput('');
    setValueError(null);
    setTimeInput(new Date());
    setFormAlert(null);
    setIsSubmitting(false);
    close();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.patch(path, {
        time: new Date(timeInput.setHours(12, 0, 0, 0)),
        value: valueInput,
      });
      reload();
      handleClose();
    } catch {
      setFormAlert({
        severity: 'error',
        title: 'Could not update measurement!',
        message:
          'Something went wrong when updating the measurement. Please contact the administrator for further support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.delete(path);
      reload();
      handleClose();
    } catch (e) {
      console.error(e);
      setFormAlert({
        severity: 'error',
        title: 'Could not delete measurement!',
        message:
          'Something went wrong when deleting the measurement. Please contact the administrator for further support.',
      });
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAbort = () => {
    handleClose();
  };

  const handleFocusLost = () => {
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleFocusLost}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Edit Measurement</DialogTitle>
      <div className={styles.content}>
        <DialogContentText id="alert-dialog-description">
          You are currently editing the measurement for goal &quot;{goalLabel}
          &quot; with the current value of &quot;{goalMeasurement.value}
          &quot; logged for{' '}
          {getAbsoluteTimestamp(new Date(goalMeasurement.time))}.
        </DialogContentText>
        <div className={styles.form}>
          <FormControl
            sx={{ m: 1 }}
            error={!!valueError}
            // disabled={!isEditable}
            className={styles.formControl}
          >
            <InputLabel id="label-label">Value</InputLabel>
            <OutlinedInput
              className={styles.input}
              id="value-input"
              label="Value"
              value={valueInput}
              onChange={(e) => setValueInput(e.target.value)}
            />
            {valueError && <FormHelperText>{valueError}</FormHelperText>}
          </FormControl>
          <FormControl
            sx={{ m: 1 }}
            // disabled={!isEditable}
            className={styles.formControl}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                id="time-input"
                value={timeInput}
                label="Date"
                inputFormat="dd/MM/yyyy"
                format="dd/MM/yyyy"
                onChange={(value) => setTimeInput(value)}
                slotProps={{
                  textField: {
                    className: styles.input,
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>
        </div>
        <div className={styles.buttons}>
          <LoadingButton
            loading={isSubmitting}
            color="error"
            variant="outlined"
            tabIndex={-1}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
          <div className={styles.subButtons}>
            <Button onClick={handleAbort} color="primary" variant="outlined">
              Cancel
            </Button>
            <LoadingButton
              loading={isSubmitting}
              color="primary"
              variant="contained"
              type="submit"
              disabled={valueInput === '' || !!valueError}
              onClick={handleSubmit}
            >
              Update
            </LoadingButton>
          </div>
        </div>
        {formAlert && <FormAlert alert={formAlert} />}
      </div>
    </Dialog>
  );
}

/**
 * Must be placed within a path that contains the goalId and teamId parameters.
 */
export function TeamEditMeasurementDialog(props) {
  const { goalId, teamId } = useParams();
  return (
    <EditMeasurementDialog
      {...props}
      path={`/user/teams/${teamId}/goals/${goalId}/measures/${props.goalMeasurement.id}`}
    />
  );
}

/**
 * Must be placed within a path that contains the goalId parameter.
 */
export function AdministratorEditMeasurementDialog(props) {
  const { goalId } = useParams();
  return (
    <EditMeasurementDialog
      {...props}
      path={`/administrator/goals/${goalId}/measures/${props.goalMeasurement.id}`}
    />
  );
}
